import { ApolloClient, useApolloClient } from '@apollo/client';
import { Align, Dialog, Label, Row, Size, Tray } from '@busybusy/webapp-react-ui';
import { CloseIcon, DeleteIcon, DownloadIcon, GpsIcon, InfoIcon, LeftArrowIcon, RightArrowIcon } from 'assets/icons';
import classNames from 'classnames';
import { Header, Panel, PanelContent } from 'components';
import SimpleMapDialog from 'components/domain/map/SimpleMapDialog/SimpleMapDialog';
import { useActiveMember } from 'hooks';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import * as React from 'react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IStorePhotos } from 'store/photos';
import { IStore, StoreAuthMemberProper } from 'store/types';
import { IMediaCollection } from 'types/MediaCollection';
import { IMediaEntry } from 'types/MediaEntry';
import IMediaEntryLocation from 'types/MediaEntryLocation';
import { Nullable } from 'types/util/Nullable';
import DateTimeFormat from 'utils/constants/dateTimeFormat';
import { dateTimeFromISOKeepZone } from 'utils/dateUtils';
import { canManagePermissionForMember } from 'utils/positionUtils';
import { getFormattedPathFromProject } from 'utils/projectUtils';
import { getFullNameFromMember } from 'utils/stringUtils';
import { MediaCollectionSourceTypes } from '__generated__/graphql';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog/DeleteConfirmationDialog';
import PhotoUtilities, { loadMediaEntry } from '../photo-utilities/photo-utilities';
import PhotoDetailsReadOnly from '../PhotoDetailsReadOnly/PhotoDetailsReadOnly';
import PhotoActionButton from './PhotoActionButton/PhotoActionButton';
import PhotoFullscreen from './PhotoFullscreen/PhotoFullscreen';
import './PhotoFullscreenViewer.scss';
import PhotoNavigationArrow from './PhotoNavigationArrow/PhotoNavigationArrow';

export interface IPhotoFullScreenViewerContainerProps {
  isOpen: boolean;
  currentMediaEntry?: IMediaEntry;
  deleteEntryHandler?: (entry: IMediaEntry, collection: IMediaCollection) => void;
  className?: string | object;
  onClose: () => void;
}
export interface IPhotoFullScreenViewerProps extends IPhotoFullScreenViewerContainerProps {
  apolloClient: ApolloClient<any>;
  authMember?: StoreAuthMemberProper;
  mediaCollections: IMediaCollection[];
}

export const PhotoFullscreenViewerContainer: React.FC<IPhotoFullScreenViewerContainerProps> = (props) => {
  const apolloClient = useApolloClient();
  const authMember = useActiveMember();
  const { mediaCollections } = useSelector<IStore, IStorePhotos>((state) => state.photos);

  return (
    <PhotoFullscreenViewer
      {...props}
      apolloClient={apolloClient}
      authMember={authMember}
      mediaCollections={mediaCollections}
    />
  );
};

export const PhotoFullscreenViewer: React.FC<IPhotoFullScreenViewerProps> = (props) => {
  const {
    apolloClient,
    authMember,
    mediaCollections,
    isOpen,
    currentMediaEntry,
    className,
    deleteEntryHandler,
    onClose,
  } = props;

  const classes = classNames('photo-fullscreen-viewer', className);

  const [t] = useTranslation();
  const [mediaEntries, setMediaEntries] = useState<IMediaEntry[]>(
    PhotoUtilities.getMediaEntriesFromCollections(mediaCollections)
  );
  const [currentPhoto, setCurrentPhoto] = useState<IMediaEntry | undefined>(currentMediaEntry);
  const [infoOpen, setInfoOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [gpsOpen, setGpsOpen] = useState<boolean>(false);
  const [performShake, setPerformShake] = useState<boolean>(false);
  const [collection, setCollection] = useState<IMediaCollection | undefined>(
    currentPhoto ? PhotoUtilities.getCollectionForMediaFromCollections(mediaCollections, currentPhoto) : undefined
  );
  const [memberCanManageCollection, setMemberCanManageCollection] = useState(false);

  const name = useMemo(() => {
    if (!isNil(collection) && collection.source === MediaCollectionSourceTypes.CompanyCam) {
      return 'CompanyCam';
    } else {
      return collection ? getFullNameFromMember(collection.createdByMember) : undefined;
    }
  }, [collection]);
  const project = collection ? getFormattedPathFromProject(collection.project, true) : undefined;

  useEffect(() => {
    if (currentPhoto) {
      setCollection(PhotoUtilities.getCollectionForMediaFromCollections(mediaCollections, currentPhoto));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhoto]);

  useEffect(() => {
    if (collection !== undefined) {
      setMemberCanManageCollection(
        canManagePermissionForMember('manageMediaCollection', authMember!, collection.createdByMember)
      );
    } else {
      setMemberCanManageCollection(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  useEffect(() => {
    const newMediaEntries = PhotoUtilities.getMediaEntriesFromCollections(mediaCollections);
    setMediaEntries(newMediaEntries);
  }, [mediaCollections]);

  useEffect(() => {
    if (currentPhoto === undefined) {
      setCurrentPhoto(currentMediaEntry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMediaEntry]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaEntries, currentPhoto, infoOpen, deleteOpen, gpsOpen]);

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.keyCode) {
      case 37: // Left
        previousClickHandler();
        break;
      case 39: // Right
        nextClickHandler();
        break;
      case 27: // Esc
        if (infoOpen) {
          infoCloseHandler();
        } else if (gpsOpen) {
          gpsCloseHandler();
        } else if (deleteOpen) {
          deleteCloseHandler();
        } else {
          onCloseHandler();
        }
        break;
      case 73: // i
        infoOpenHandler();
        break;
      case 68: // d
        downloadImageHandler();
        break;
      case 76: // l
        if (currentPhoto && currentPhoto.location) {
          gpsOpenHandler();
        }
        break;
      default:
        break;
    }
  };

  const isPreviousDisabled = () => {
    return mediaEntries.indexOf(currentPhoto!) === 0;
  };

  const isNextDisabled = () => {
    return mediaEntries.indexOf(currentPhoto!) === mediaEntries.length - 1;
  };

  function shake() {
    setPerformShake(true);
    setTimeout(() => {
      setPerformShake(false);
    }, 300);
  }

  const previousClickHandler = () => {
    const currentIndex = mediaEntries.indexOf(currentPhoto!);
    if (currentIndex > 0) {
      const prevPhoto = mediaEntries[currentIndex - 1];
      return setCurrentPhoto(prevPhoto);
    } else {
      shake();
    }
  };

  const nextClickHandler = () => {
    const currentIndex = mediaEntries.indexOf(currentPhoto!);
    if (currentIndex < mediaEntries.length - 1) {
      const nextPhoto = mediaEntries[currentIndex + 1];
      return setCurrentPhoto(nextPhoto);
    } else {
      shake();
    }
  };

  const onCloseHandler = () => {
    setCurrentPhoto(undefined);
    onClose();
  };

  const downloadImageHandler = () => {
    if (currentPhoto && collection) {
      // due to CORS issues, we need unused urls before trying to download the photos
      return loadMediaEntry(apolloClient, currentPhoto.id).then((freshMediaEntries) => {
        if (freshMediaEntries) {
          return PhotoUtilities.downloadMediaEntry({
            encompassingCollection: collection!,
            mediaEntry: freshMediaEntries.shift()!,
          });
        }
      });
    }
  };

  function renderInfoDialog() {
    // TODO validate that the new iso timestamps are correct
    return (
      <PhotoDetailsReadOnly
        date={
          currentPhoto && currentPhoto.captureDate
            ? DateTime.fromISO(currentPhoto.captureDate, { zone: 'utc' }).toSeconds()
            : 0
        }
        description={collection ? collection.description : ''}
        isOpen={infoOpen}
        layer={2}
        onClose={infoCloseHandler}
        photoTaken={
          currentPhoto && currentPhoto.captureDate
            ? DateTime.fromISO(currentPhoto.captureDate, { zone: 'utc' }).toSeconds()
            : 0
        }
        projectPath={project}
        uploadedBy={name ? name : ''}
      />
    );
  }

  function infoOpenHandler() {
    setInfoOpen(true);
  }

  function infoCloseHandler() {
    setInfoOpen(false);
  }

  function getInfoWindowBody(location: IMediaEntryLocation, captureDate: Nullable<string>) {
    const body: ReactNode[] = [
      <p key={location.id}>
        {(captureDate ? dateTimeFromISOKeepZone(captureDate) : dateTimeFromISOKeepZone(location.timeLocal)).toFormat(
          DateTimeFormat.dateAtTime
        )}
      </p>,
    ];
    return body;
  }

  function renderMapDialog() {
    if (currentPhoto && currentPhoto.location) {
      return (
        <SimpleMapDialog
          isOpen={gpsOpen}
          onClose={gpsCloseHandler}
          size={Size.LARGE}
          pinLocations={[
            {
              id: currentPhoto.location.id,
              lat: currentPhoto.location.latitude,
              lng: currentPhoto.location.longitude,
              infoWindowTitle: project,
              infoWindowBody: getInfoWindowBody(currentPhoto.location, currentPhoto.captureDate ?? null),
            },
          ]}
        />
      );
    }
  }

  function gpsOpenHandler() {
    setGpsOpen(true);
  }

  function gpsCloseHandler() {
    setGpsOpen(false);
  }

  function deleteOpenHandler() {
    setDeleteOpen(true);
  }

  function deleteCloseHandler() {
    setDeleteOpen(false);
  }

  function deleteHandler() {
    currentPhoto && collection && deleteEntryHandler ? deleteEntryHandler(currentPhoto, collection) : onCloseHandler();

    if (!isPreviousDisabled()) {
      previousClickHandler();
    } else if (isPreviousDisabled() && !isNextDisabled()) {
      nextClickHandler();
    } else if (isPreviousDisabled() && isNextDisabled()) {
      onCloseHandler();
    }
    deleteCloseHandler();
  }

  function renderDeleteDialog() {
    return (
      <DeleteConfirmationDialog
        isOpen={deleteOpen}
        onClose={deleteCloseHandler}
        onDelete={deleteHandler}
        title={t('Delete photo') + '?'}
      >
        {t('If you delete this photo it will be gone forever. Are you sure you want to continue?')}
      </DeleteConfirmationDialog>
    );
  }

  function renderHeader() {
    const gpsButton =
      currentPhoto && currentPhoto.location ? (
        <PhotoActionButton tooltip={t('Location')} svg={GpsIcon} onClick={gpsOpenHandler} />
      ) : undefined;
    const deleteButton = memberCanManageCollection ? (
      <PhotoActionButton tooltip={t('Delete')} svg={DeleteIcon} onClick={deleteOpenHandler} />
    ) : undefined;
    return (
      <Header className="header">
        <span>
          {mediaEntries.indexOf(currentPhoto!) + 1} {t('of')} {mediaEntries.length}
        </span>
        <Tray>
          {gpsButton}
          {deleteButton}
          <PhotoActionButton tooltip={t('Info')} svg={InfoIcon} onClick={infoOpenHandler} />
          <PhotoActionButton tooltip={t('Download')} svg={DownloadIcon} onClick={downloadImageHandler} />
          <PhotoActionButton tooltip={t('Close')} svg={CloseIcon} onClick={onCloseHandler} />
        </Tray>
      </Header>
    );
  }

  const renderFooter = (
    <Header className="footer">
      <div />
      <div style={{ height: '100%' }}>
        <Label className="fw-semi-bold pt-3">{name}</Label>
        <Label className="fw-light">{project}</Label>
      </div>
      <div />
    </Header>
  );

  const renderMiddlePanel = (
    <PanelContent className="panel-content">
      <Row align={Align.CENTER} className="middle-row">
        <PhotoNavigationArrow
          direction="left"
          disabled={isPreviousDisabled}
          icon={LeftArrowIcon}
          onClick={previousClickHandler}
        />
        <PhotoFullscreen performShake={performShake} photo={currentPhoto} />
        <PhotoNavigationArrow
          direction="right"
          disabled={isNextDisabled}
          icon={RightArrowIcon}
          onClick={nextClickHandler}
        />
      </Row>
    </PanelContent>
  );

  return (
    <Dialog hasDismiss={false} className={classes} size="full" isOpen={isOpen} onClose={onCloseHandler}>
      <Panel>
        {renderDeleteDialog()}
        {renderMapDialog()}
        {renderInfoDialog()}
        {renderHeader()}
        {renderMiddlePanel}
        {renderFooter}
      </Panel>
    </Dialog>
  );
};

export default PhotoFullscreenViewerContainer;
